import React from "react";
import { useTranslation } from "react-i18next";
import useResetScroll from "../../utils/useResetScroll";
import { Shield, Mail, AlertCircle } from "lucide-react";

export default function PrivacyPolicy() {
  useResetScroll();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl shadow-blue-900/10 overflow-hidden border border-slate-100 relative">
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
        
        <div className="pt-8 pb-6 px-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-xl mb-3">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-1">
            {t("Privacy Policy")}
          </h1>
          <p className="text-slate-500 text-sm font-medium">Ota Life</p>
        </div>

        <div className="px-6 pb-6 space-y-6 text-slate-700 text-sm leading-relaxed">
          <p>
            {t("Ota Life (\"we\", \"our\", \"us\") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Ota Life mobile application and related services.")}
          </p>
          <p className="text-slate-600 text-xs italic">
            {t("By using the app, you agree to the practices described in this policy.")}
          </p>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              1. {t("Information We Collect")}
            </h2>
            
            <h3 className="font-semibold text-slate-800 mb-2">1.1 {t("Information You Provide")}</h3>
            <ul className="space-y-1 mb-4 ml-4">
              <li>• {t("Email address")}</li>
              <li>• {t("Login information (Google, Apple, email verification)")}</li>
              <li>• {t("Nickname (optional)")}</li>
              <li>• {t("Date of Birth (optional)")}</li>
              <li>• {t("Profile photo (optional)")}</li>
              <li>• {t("Phone number (for ticket purchases)")}</li>
              <li>• {t("Ticket selections and event preferences")}</li>
              <li>• {t("User-submitted ideas, forms, images, and content")}</li>
              <li>• {t("Payment-related information (handled by Razorpay; we do not store card details)")}</li>
            </ul>

            <h3 className="font-semibold text-slate-800 mb-2">1.2 {t("Automatically Collected Data")}</h3>
            <ul className="space-y-1 mb-4 ml-4">
              <li>• {t("Device information and OS")}</li>
              <li>• {t("App usage data")}</li>
              <li>• {t("Crash logs and diagnostics (via Sentry)")}</li>
              <li>• {t("Notification tokens")}</li>
              <li>• {t("IP address and approximate location")}</li>
            </ul>

            <h3 className="font-semibold text-slate-800 mb-2">1.3 {t("Media & Storage Permissions")}</h3>
            <p className="mb-2">{t("We request access to:")}</p>
            <ul className="space-y-1 mb-2 ml-4">
              <li>• {t("Camera — to upload images")}</li>
              <li>• {t("Photo/Media Library — to select and save images (e.g., QR tickets)")}</li>
              <li>• {t("Storage — for saving QR tickets and downloaded content")}</li>
              <li>• {t("Notifications — for reminders, events, and quiz alerts")}</li>
            </ul>
            <p className="text-xs text-slate-600 italic">{t("We do not access media without your action or permission.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              2. {t("How We Use Your Information")}
            </h2>
            <ul className="space-y-1 ml-4">
              <li>• {t("Create and manage your account")}</li>
              <li>• {t("Process ticket purchases & workshop registrations")}</li>
              <li>• {t("Deliver quizzes, events, notifications, and content")}</li>
              <li>• {t("Enable idea submission & community voting")}</li>
              <li>• {t("Improve app performance & user experience")}</li>
              <li>• {t("Provide customer support")}</li>
              <li>• {t("Prevent fraud & abuse")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              3. {t("Ticketing & Payments")}
            </h2>
            <p>{t("Payments are securely processed by Razorpay. We never store your payment card information.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              4. {t("Quizzes, Events & Rewards")}
            </h2>
            <p className="mb-2">{t("Participation in quizzes and events may involve:")}</p>
            <ul className="space-y-1 ml-4">
              <li>• {t("Tracking quiz participation and scores")}</li>
              <li>• {t("Delivering prizes or rewards")}</li>
              <li>• {t("Displaying event attendance")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              5. {t("Reels & Content Browsing")}
            </h2>
            <p>{t("Reels and posts are curated content. Currently, no public interaction features are enabled.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              6. {t("Data Sharing")}
            </h2>
            <p className="font-semibold mb-2">{t("We do not sell your personal data.")}</p>
            <p className="mb-2">{t("We may share limited data with:")}</p>
            <ul className="space-y-1 ml-4">
              <li>• {t("Payment processors (Razorpay)")}</li>
              <li>• {t("Analytics and error reporting (Sentry)")}</li>
              <li>• {t("Legal authorities if required by law")}</li>
            </ul>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              7. {t("Data Security")}
            </h2>
            <p>{t("We use industry-standard encryption, secure servers, and restricted access controls to protect your data.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              8. {t("Your Rights")}
            </h2>
            <p className="mb-2">{t("You may:")}</p>
            <ul className="space-y-1 ml-4 mb-3">
              <li>• {t("Access or update your profile")}</li>
              <li>• {t("Request deletion of your data")}</li>
              <li>• {t("Withdraw permissions anytime via device settings")}</li>
            </ul>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mt-3">
              <p className="text-xs text-blue-800">
                {t("To request deletion, go to My Profile section in the app or contact support at:")} <a href="mailto:otatokyo.official@gmail.com" className="font-semibold underline">otatokyo.official@gmail.com</a>
              </p>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              9. {t("Children's Privacy")}
            </h2>
            <p>{t("Ota Life is not intended for children under 13. We do not knowingly collect data from minors.")}</p>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">
              10. {t("Policy Updates")}
            </h2>
            <p>{t("We may update this policy periodically. Continued use of the app means acceptance of the updated policy.")}</p>
          </div>
        </div>

        <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
          <p className="text-xs text-slate-600 mb-2">{t("Questions or Concerns?")}</p>
          <a
            href="mailto:otatokyo.official@gmail.com"
            className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:text-blue-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            otatokyo.official@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}
